/** v1's Render backend, called v1's way (auth-users.js apiFetch, verbatim
 * semantics):
 *   - Authorization: Bearer <supabase access token> — the server validates it
 *     against the same project and resolves the org itself.
 *   - Render free tier sleeps: 502/503/504 (or a network error) means "waking
 *     up" → notify once, wait 10s, retry ONCE. v1's exact dance.
 *   - Errors surface the server's j.detail (v1 toasts String(j.detail||status)
 *     .slice(0,120)) — BackendError carries it for the UI to show.
 * No blind write retries beyond the single wake retry: Phase A only ever
 * calls read/estimate endpoints; anything that live-fires waits for Phase B.
 */
import { API_BASE } from "../config";

export class BackendError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
  }
}

export interface BackendOptions {
  method?: "GET" | "POST" | "PUT";
  body?: unknown;
  /** per-attempt timeout; comps/estimate scrape for 15-40s, so default 90s */
  timeoutMs?: number;
  signal?: AbortSignal | undefined;
  /** called when the server looks asleep and we're waiting out the wake */
  onWake?: () => void;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function backendFetch<T = unknown>(
  token: string | null,
  path: string,
  opts: BackendOptions = {},
): Promise<T> {
  const url = API_BASE + path;
  const timeoutMs = opts.timeoutMs ?? 90000;
  for (let attempt = 0; ; attempt++) {
    const ctl = new AbortController();
    const onOuter = () => ctl.abort();
    opts.signal?.addEventListener("abort", onOuter);
    const t = setTimeout(() => ctl.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        method: opts.method ?? "GET",
        headers: {
          ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: opts.body !== undefined ? JSON.stringify(opts.body) : null,
        signal: ctl.signal,
      });
      if ((res.status === 502 || res.status === 503 || res.status === 504) && attempt === 0) {
        opts.onWake?.();
        await sleep(10000);
        continue;
      }
      const text = await res.text();
      let data: unknown = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = text;
      }
      if (!res.ok) {
        const detail =
          data !== null && typeof data === "object" && typeof (data as { detail?: unknown }).detail === "string"
            ? ((data as { detail: string }).detail)
            : String(res.status);
        throw new BackendError(res.status, detail.slice(0, 120));
      }
      return data as T;
    } catch (e) {
      if (e instanceof BackendError) throw e;
      if (opts.signal?.aborted) throw e;
      if (attempt === 0) {
        // network error / timeout — same wake dance as 502
        opts.onWake?.();
        await sleep(10000);
        continue;
      }
      throw new BackendError(0, "Could not reach the backend");
    } finally {
      clearTimeout(t);
      opts.signal?.removeEventListener("abort", onOuter);
    }
  }
}
